'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

// ============================================================================
// Types & Schema
// ============================================================================

const bridgeFormSchema = z.object({
  clientId: z.string().min(1, 'Client ID is required').trim(),
  clientSecret: z.string().min(1, 'Client Secret is required').trim(),
})

type FormValues = z.infer<typeof bridgeFormSchema>

interface BridgeFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function BridgeForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: BridgeFormProps) {
  // Show bullets if existing credential exists
  const hasExistingClientSecret =
    defaultValues.credentials?.clientSecret &&
    defaultValues.credentials.clientSecret.length > 0

  const form = useForm<FormValues>({
    resolver: zodResolver(bridgeFormSchema),
    mode: 'all',
    defaultValues: {
      clientId: defaultValues.credentials?.clientId ?? '',
      clientSecret: hasExistingClientSecret ? '•'.repeat(20) : '',
    },
  })

  const { register, handleSubmit, formState: { errors, touchedFields }, watch, setValue } = form

  const handleFormSubmit = async (data: FormValues) => {
    const { clientId, clientSecret } = data
    await onSubmit({
      credentials: {
        clientId: touchedFields.clientId ? clientId : (defaultValues.credentials?.clientId ?? ''),
        clientSecret: touchedFields.clientSecret ? clientSecret : (defaultValues.credentials?.clientSecret ?? ''),
      },
    })
  }

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">{/* API Credentials */}
      <Card>
        <CardHeader>
          <CardTitle>API Credentials</CardTitle>
          <CardDescription>
            Enter your Bridge credentials
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="clientId">Client ID</Label>
            <Input
              id="clientId"
              disabled={isLoading}
              {...register('clientId')}
            />
            {touchedFields.clientId && errors.clientId && (
              <p className="text-xs text-destructive">The client ID is required</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="clientSecret">Client Secret</Label>
            <Input
              id="clientSecret"
              disabled={isLoading}
              {...register('clientSecret')}
            />
            {touchedFields.clientSecret && errors.clientSecret && (
              <p className="text-xs text-destructive">The client secret is required</p>
            )}
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
